import React, {FC, ReactNode, useEffect, useRef, useState} from 'react';
import styled, {css} from 'styled-components';

import {globalWindow} from '../../../../libs/shared/helpers/src/browser/browserHelpers';
import {buildInteractionIdProps} from '../../../../libs/shared/helpers/src/interaction/interactionHelpers';
import {ClickEventHandler, Handler, HandlerOf} from '../../../../libs/shared/helpers/src/react/reactHelpers';
import {ChromelessButton} from '../../../../libs/shared/ui/src/components/buttons/chromelessButton';
import {Icon} from '../../../../libs/shared/ui/src/components/icon/icon';
import {fontWeights} from '../../../../libs/shared/ui/src/styles/fontStyles';
import {useUserTheme} from '../../../../libs/shared/ui/src/theme/userThemeProvider';

/*
 * Constants.
 */

const scrollThreshold = 20; // px

/*
 * Props.
 */

export interface AccordionSectionData {
  id: string;
  title: ReactNode;
  titleAction?: ReactNode;
  content: ReactNode;
  isExpandedByDefault?: boolean;
  onSectionToggled?: HandlerOf<boolean>;
  titlePadding?: string;
  sectionPadding?: number;
  shouldScrollOnExpand?: boolean;
}

interface AccordionSectionProps extends Omit<AccordionSectionData, 'isExpandedByDefault'> {
  isExpanded: boolean;
  disableBorders?: boolean;
  onTitleClicked: Handler;
  renderSectionTitle?: (title: ReactNode, onClick: ClickEventHandler, isOpen: boolean) => ReactNode;
}

/*
 * Styles.
 */

const StyledAccordionSectionDiv = styled.div<{$disableBorders?: boolean}>`
  display: flex;
  flex-direction: column;

  /* Section dividers */
  border-bottom: ${(p) => (p.$disableBorders ? 'none' : `1px solid ${p.theme.greys.shade30}`)};
`;

interface StyledAccordionSectionContainerDivProps {
  $titlePadding?: string;
}

const StyledAccordionSectionContainerDiv = styled.div<StyledAccordionSectionContainerDivProps>`
  display: flex;
  flex-flow: row;
  padding: ${(p) => p.$titlePadding ?? '11px 9px 9px 13px'};
  margin: 7px;
`;

const StyledAccordionSectionTitleButton = styled(ChromelessButton)`
  display: flex;
  align-items: center;
  flex: 1;

  color: ${(p) => p.theme.greys.shade80};
  font-weight: ${fontWeights.bold};

  &:hover {
    color: ${(p) => p.theme.greys.shade90};
  }
`;

const StyledSectionTitleSpacerDiv = styled.div`
  flex: 1;
`;

const defaultSectionPaddingPx = 20;
interface AccordionSectionContentDivProps {
  $sectionPadding: number;
}

const StyledAccordionSectionContentDiv = styled.div<AccordionSectionContentDivProps>`
  margin-top: -11px;
  ${(p) => getSectionPadding(p.$sectionPadding)}
`;

const getSectionPadding = (padding: number) => css`
  padding: 0px ${padding}px ${padding}px ${padding}px;
`;

const StyledChevronIcon = styled(Icon)`
  margin-left: 4px;
  height: 20px;
`;

/*
 * Component.
 */

export const accordionSectionIDDataAttribute = 'data-accordion-section-id';

export const AccordionSection: FC<AccordionSectionProps> = (props) => {
  const theme = useUserTheme();
  const {isExpanded, shouldScrollOnExpand, disableBorders, renderSectionTitle} = props;
  const [isMouseOver, setIsMouseOver] = useState(false);
  const [shouldAutoScrollContent, setShouldAutoScrollContent] = useState(false);
  const contentRef = useRef<HTMLDivElement>(null);
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (!shouldScrollOnExpand || !shouldAutoScrollContent || !contentRef?.current || !titleRef?.current) {
      return;
    }

    // If the content isn't visible (including a small threshold), scroll the title into view
    if (contentRef.current.getBoundingClientRect().top + scrollThreshold > globalWindow.innerHeight) {
      titleRef.current.scrollIntoView({block: 'start'});
    }
  }, [shouldScrollOnExpand, shouldAutoScrollContent]);

  const onClick: ClickEventHandler = (event) => {
    if (event.defaultPrevented) {
      return;
    }

    props.onTitleClicked();

    // When the accordion is collapsed and the user clicks to expand, set to true so that it will fire the effect to auto scroll
    // When the accordion is expanded and the user clicks to collapse, set to false to reset
    if (shouldScrollOnExpand) {
      setShouldAutoScrollContent(!isExpanded);
    }
  };

  const renderTitle = () => {
    if (renderSectionTitle) {
      return renderSectionTitle(props.title, onClick, isExpanded);
    }

    return (
      <StyledAccordionSectionContainerDiv $titlePadding={props.titlePadding}>
        <StyledAccordionSectionTitleButton
          interactionId={`accordion-section-${props.id}`}
          onClick={onClick}
          onMouseEnter={() => setIsMouseOver(true)}
          onMouseLeave={() => setIsMouseOver(false)}
          ref={titleRef}
        >
          {props.title}
          <StyledChevronIcon
            name={isExpanded ? 'chevronCollapseSmall' : 'chevronExpandSmall'}
            color={isMouseOver ? theme.greys.shade90 : theme.greys.shade70}
          />
          <StyledSectionTitleSpacerDiv />
        </StyledAccordionSectionTitleButton>
        {props.titleAction}
      </StyledAccordionSectionContainerDiv>
    );
  };

  const renderContent = () => (
    <StyledAccordionSectionContentDiv
      $sectionPadding={props.sectionPadding || defaultSectionPaddingPx}
      ref={contentRef}
      {...buildInteractionIdProps(`accordion-section-content-${props.id}`)}
    >
      {props.content}
    </StyledAccordionSectionContentDiv>
  );

  return (
    <StyledAccordionSectionDiv $disableBorders={disableBorders}>
      {renderTitle()}
      {props.isExpanded && renderContent()}
    </StyledAccordionSectionDiv>
  );
};
